import React, { useEffect, useState } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import { Button, Card, notification, Row, Col, Input, Select, Space } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'

import { fetchVLPCustomCardById, updateVLPCustomCard, resetVLPCustomCardActionStatus } from '~actions'
import { ImageUploader, ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { MultiSelectDropdown, PageLayout, Spinner } from '~stories'

import styles from './EditVlpCustomCard.module.css'

const PAGE_TITLE = 'Update VLP Card'
const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.'
}

const EditVlpCustomCard = ({
  allVenues,
  msgFlagsList,
  fetchVLPCustomCardById,
  resetVLPCustomCardActionStatus,
  route,
  vlpCard,
  updateMessage,
  updateVLPCustomCard,
  updateStatus
}) => {
  const [isDataLoaded, setIsDataLoaded] = useState()
  const [vlpCardId, setVlpCardId] = useState('')
  const [vlpCardToEdit, setVlpCardToEdit] = useState({})
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [selectedFlags, setSelectedFlags] = useState([])

  useEffect(() => {
    const vlpCardId = idx(route, _ => _.match.params.id)
    if (vlpCardId) {
      setIsDataLoaded(false)
      fetchVLPCustomCardById(vlpCardId)
      setVlpCardId(vlpCardId)
    }
  }, [route, fetchVLPCustomCardById])

  useEffect(() => {
    if (vlpCard) {
      setVlpCardToEdit(vlpCard)
      const selectedFlags = vlpCard?.flags ? vlpCard?.flags.map(({ title }) => title) : []
      setSelectedFlags(selectedFlags)
      setIsDataLoaded(true)
    }
  }, [vlpCard])

  useEffect(() => {
    if (updateStatus && updateMessage) {
      setIsDataLoaded(true)
    } else if (!updateStatus && updateMessage) {
      setIsDataLoaded(true)
      notification.error({ message: updateMessage })
      resetVLPCustomCardActionStatus()
    }
  }, [updateStatus, updateMessage, resetVLPCustomCardActionStatus])

  const handleSubmit = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    setIsDataLoaded(false)
    const updatedVlpCard = {
      ...vlpCardToEdit
    }
    updateVLPCustomCard(vlpCardId, updatedVlpCard, accessToken)
  }

  const keepEditingVlpCard = (
    <Route key="editVlpCards" render={({ history }) => (
      <Button
        onClick={() => {
          resetVLPCustomCardActionStatus()
          history.push(`/venues/venue-landing-cards/${vlpCardId}/edit`)
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToListVlpCardsButton = (
    <Route key="listVlpCards" render={({ history }) => (
      <Button
        onClick={() => {
          resetVLPCustomCardActionStatus()
          history.push('/venues/venue-landing-cards')
        }}
        type="primary"
      >
        Go to VLP Cards List
      </Button>
    )} />
  )

  const handleIconChange = (imgObj) => {
    const clonedVlpCard = { ...vlpCardToEdit }
    clonedVlpCard.default_image = { ...clonedVlpCard.default_image, ...imgObj }
    setVlpCardToEdit(clonedVlpCard)
  }

  const handleImageUrlUpdate = (imageUrl) => {
    const clonedVlpCard = { ...vlpCardToEdit }
    clonedVlpCard.default_image = { ...clonedVlpCard.default_image, url: imageUrl }
    setVlpCardToEdit(clonedVlpCard)
  }

  const handleFlagsUpdate = (selectedItem) => {
    setSelectedFlags(selectedItem)
    const selectedFlags = msgFlagsList.filter(flag => [selectedItem].includes(flag.title))
    setVlpCardToEdit({ ...vlpCardToEdit, flags: selectedFlags })
  }

  const handleDeleteCta = (index) => {
    const ctas = [...(vlpCardToEdit.ctas || [])]
    ctas.splice(index, 1)
    setVlpCardToEdit({ ...vlpCardToEdit, ctas })
  }

  const handleCtaChange = (index, field, value) => {
    const ctas = [...(vlpCardToEdit.ctas || [])]
    ctas[index] = { ...ctas[index], [field]: value }
    setVlpCardToEdit({ ...vlpCardToEdit, ctas })
  }

  const handleAddCta = () => {
    if ((vlpCardToEdit.ctas || []).length < 2) {
      setVlpCardToEdit({
        ...vlpCardToEdit,
        ctas: [...(vlpCardToEdit.ctas || []), { label: '', url: '' }]
      })
    }
  }

  const handleModalClose = () => {
    resetVLPCustomCardActionStatus()
  }

  const updateVlpCustomCardButton = <Button className={styles.updateActionButton} onClick={handleSubmit} type='primary'>
    Update
  </Button>

  return (
    <PageLayout extra={[updateVlpCustomCardButton]} title={PAGE_TITLE}>
      <div className={styles['new-vlp-custom-card-container']}>
        {isDataLoaded === false && <Spinner />}
        <Card style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <label htmlFor="uniqueNameInput" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Id (unique, not editable):</label>
                <Input
                  disabled
                  id="uniqueNameInput"
                  value={vlpCardToEdit?.id}
                />
                <label htmlFor="titleInput" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Name:</label>
                <Input
                  id="titleInput"
                  onChange={e => setVlpCardToEdit({ ...vlpCardToEdit, name: e.target.value })}
                  placeholder="Enter name"
                  value={vlpCardToEdit.name}
                />
              </Space>
            </Col>
            <Col span={12}>
              <Space direction="vertical" style={{ width: '100%' }}>

                <label htmlFor="venueSelect" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Venue:</label>
                <Select
                  disabled
                  id="venueSelect"
                  onChange={value => setVlpCardToEdit({ ...vlpCardToEdit, venue_id: value })}
                  placeholder="Select a venue"
                  style={{ width: '100%' }}
                  value={vlpCardToEdit.venue_id}
                >
                  {Array.isArray(allVenues) && allVenues.map(venue => (
                    <Select.Option key={venue.value} value={venue.value}>
                      {venue.label}
                    </Select.Option>
                  ))}
                </Select>
                <label htmlFor="subtitleInput" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Subtitle:</label>
                <Input
                  id="subtitleInput"
                  onChange={e => setVlpCardToEdit({ ...vlpCardToEdit, sub_title: e.target.value })}
                  placeholder="Enter subtitle"
                  value={vlpCardToEdit.sub_title}
                />
              </Space>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <label htmlFor="defaultImageUploader" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Flags&nbsp;&nbsp;(Select one item from the dropdown):</label>
              <MultiSelectDropdown
                allowClear={true}
                displayField="title"
                dropdownOptions={msgFlagsList || []}
                handleChange={handleFlagsUpdate}
                selectedItems={selectedFlags || []}
                valueField="title"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={16}>
              <div style={{ marginTop: 16 }}>
                <label style={{ marginBottom: 8, marginRight: 12 }}>CTAs (max 2):</label>
                <Button disabled={(vlpCardToEdit.ctas || []).length >= 2} onClick={handleAddCta} style={{ marginBottom: 8 }}
                  type="primary">
                    Add CTA
                </Button>
                {(vlpCardToEdit.ctas || []).map((cta, idx) => (
                  <div key={idx} style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Input
                      onChange={e => handleCtaChange(idx, 'label', e.target.value)}
                      placeholder="CTA Label"
                      style={{ width: '40%' }}
                      value={cta.label}
                    />
                    <Input
                      onChange={e => handleCtaChange(idx, 'url', e.target.value)}
                      placeholder="CTA URL"
                      style={{ width: '50%' }}
                      value={cta.url}
                    />
                    <Button
                      aria-label="Delete CTA"
                      icon={<CloseOutlined />}
                      onClick={() => handleDeleteCta(idx)}
                      onMouseEnter={e => { e.currentTarget.style.background = '#d9363e' }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#ff4d4f' }}
                      style={{
                        background: '#ff4d4f',
                        color: '#fff',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(255,77,79,0.15)',
                        transition: 'background 0.3s',
                        fontSize: 16,
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      type="text"
                    />
                  </div>
                ))}
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <label htmlFor="defaultImageUploader" style={{ marginBottom: 8, marginTop: 16, display: 'block' }}>Default Image:</label>
              <ImageUploader
                alt={vlpCardToEdit?.default_image?.alt}
                handleIconChange={imageObj => handleIconChange(imageObj)}
                handleUpload={imageUrl => handleImageUrlUpdate(imageUrl)}
                logoUrl={vlpCardToEdit?.default_image?.url}
              />
            </Col>
          </Row>
        </Card>
      </div>
      {
        updateStatus &&
        <ResultModal
          actions={[
            goToListVlpCardsButton,
            keepEditingVlpCard
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
    </PageLayout>
  )
}

const mapStateToProps = ({ vlpCards }) => {
  console.log('vlpCards', vlpCards)
  return ({
    vlpCard: vlpCards?.vlpCard,
    newVlpCustomCardId: vlpCards?.newVlpCustomCardId,
    updateMessage: vlpCards?.message,
    allVenues: vlpCards?.allVenues,
    msgFlagsList: vlpCards?.allFlags,
    updateStatus: vlpCards?.success
  })
}

const mapDispatchToProps = {
  fetchVLPCustomCardById,
  updateVLPCustomCard,
  resetVLPCustomCardActionStatus
}

EditVlpCustomCard.propTypes = {
  allVenues: PropTypes.array,
  fetchVLPCustomCardById: PropTypes.func,
  msgFlagsList: PropTypes.array,
  newVlpCustomCardId: PropTypes.string,
  resetVLPCustomCardActionStatus: PropTypes.func,
  route: PropTypes.object.isRequired,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool,
  updateVLPCustomCard: PropTypes.func,
  vlpCard: PropTypes.object
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditVlpCustomCard)
